<template>
    <span
        v-if="disabled"
        ref="linkElement"
        class="category-link-disabled"
        role="link"
        aria-disabled="true"
        :aria-busy="pending || undefined">
        <slot />
        <button v-if="failed" type="button" class="ml-2 text-xs underline" @click.stop.prevent="retry">
            Opnieuw proberen
        </button>
    </span>
    <NuxtLink v-else ref="linkElement" :to="destination" :prefetch="enabled ? false : undefined" @click="emit('click', $event)"><slot /></NuxtLink>
</template>

<script setup lang="ts">
    import type { RouteLocationRaw } from 'vue-router';
    const emit = defineEmits<{ click: [event: MouseEvent] }>();
    const props = defineProps<{
        to?: RouteLocationRaw;
        categoryId?: string;
        categoryPath?: string;
        search?: string;
        observeParent?: boolean;
    }>();
    const enabled = useKeepFilters();
    const linkElement = ref<any>();
    const visible = ref(false);
    let observer: IntersectionObserver | undefined;
    let stopWatching: (() => void) | undefined;
    onMounted(() => {
        if (typeof IntersectionObserver === 'undefined') {
            stopWatching = watch(enabled, value => { visible.value = value; }, { immediate: true });
            return;
        }
        observer = new IntersectionObserver(entries => {
            visible.value = entries.some(entry => entry.isIntersecting);
        });
        stopWatching = watch([linkElement, enabled], ([element, active]) => {
            observer!.disconnect();
            if (!active) { visible.value = false; return; }
            const link = element?.$el || element;
            // Sortable quick links check the whole visible row, including links outside its scroll viewport.
            const node = props.observeParent ? link?.parentElement : link;
            if (node instanceof Element) observer!.observe(node);
        }, { immediate: true, flush: 'post' });
    });
    onBeforeUnmount(() => {
        stopWatching?.();
        observer?.disconnect();
    });
    const {
        to: destination,
        disabled,
        pending,
        failed,
        retry,
    } = useCategoryNavigation(
        toRef(props, 'to'),
        computed(() => ({ categoryId: props.categoryId, categoryPath: props.categoryPath, search: props.search })),
        visible,
    );
</script>

<style scoped>
    .category-link-disabled {
        opacity: 0.45;
        filter: grayscale(1);
        color: #777 !important;
        cursor: not-allowed !important;
    }
    .category-link-disabled button {
        cursor: pointer;
    }
</style>
